import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import type { FormProps } from 'antd';
import { Form } from 'antd';
import { toNumber } from 'lodash';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

export type TieredFee = {
  fee: string;
  from: string;
  to: string;
};

export type TieredFeeFormValues = {
  tieredFees: Array<TieredFee>;
};

type TieredFeeTableProps = {
  isOpen: boolean;
  toggle: () => void;
  defaultValues: TieredFeeFormValues;
  onSubmit?: (values: Array<TieredFee>) => void;
  editable?: boolean;
};

const TieredFeeTableModal: React.FC<TieredFeeTableProps> = ({
  isOpen,
  toggle,
  defaultValues,
  onSubmit,
  editable = true,
}: TieredFeeTableProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm<TieredFeeFormValues>();
  const addNewRef = useRef<((defaultValue?: any) => void) | null>(null);
  const watchFeeList = Form.useWatch('tieredFees', form);
  const [isEditing, setIsEditing] = useState(false);

  const onFinish: FormProps<TieredFeeFormValues>['onFinish'] = (values) => {
    onSubmit?.(values.tieredFees);
    setIsEditing(false);
  };

  const onFinishFailed: FormProps<TieredFeeFormValues>['onFinishFailed'] =
    () => {
      // noop - form validation handles error display
    };

  const columns = [
    {
      key: 'from',
      title: messages['common.from'],
      dataIndex: 'from',
    },
    {
      key: 'to',
      title: messages['common.to'],
      dataIndex: 'to',
    },
    {
      key: 'fee',
      title: messages['common.fee'],
      dataIndex: 'fee',
      render: (value: string) =>
        formatMoney(toNumber(value), showCurrencyByCountry(payrollCountry)),
    },
  ];

  return (
    <AppModalContainer
      title={messages['common.tieredFeeTable']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        !editable
          ? [
              {
                name: messages['common.close'],
                variant: 'outlined',
                onClick: toggle,
              },
            ]
          : isEditing
            ? [
                {
                  name: messages['common.save'],
                  buttonType: 'primary',
                  onClick: () => form.submit(),
                },
              ]
            : [
                {
                  name: messages['common.close'],
                  variant: 'outlined',
                  onClick: toggle,
                },
                {
                  name: messages['common.edit'],
                  buttonType: 'primary',
                  onClick: () => setIsEditing(true),
                },
              ]
      }
      minHeight="200px"
    >
      {!isEditing ? (
        <AppCard className="pb-4" title={messages['common.fees']}>
          <AppTableContainer
            data={defaultValues.tieredFees ?? []}
            rowKey="key"
            pagination={false}
            loading={false}
            page={0}
            total={defaultValues?.tieredFees?.length || 0}
            pageSize={defaultValues?.tieredFees?.length || 0}
            scroll={{ x: 300 }}
            titleEmpty={messages['common.tieredFee.empty']}
            columns={columns}
          />
        </AppCard>
      ) : (
        <AppFormContainer
          form={form}
          initialValues={defaultValues ?? []}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          wrapperCol={{ span: 24 }}
        >
          <AppCard
            className="pb-4"
            extra={
              <AppButton
                leftIcon={<PlusOutlined />}
                name={messages['common.addNew']}
                type="primary"
                onClick={() =>
                  addNewRef.current?.({
                    fee: '',
                    from:
                      watchFeeList?.length > 0 &&
                      watchFeeList[watchFeeList.length - 1].to
                        ? Math.round(
                            (toNumber(
                              watchFeeList[watchFeeList.length - 1].to ?? 0,
                            ) +
                              0.01) *
                              100,
                          ) / 100
                        : '',
                    to: '',
                  })
                }
              />
            }
            title={messages['common.fees']}
          >
            <Form.List name="tieredFees">
              {(fields, { add, remove }) => {
                addNewRef.current = add;
                return (
                  <div>
                    <AppTableContainer
                      data={fields}
                      rowKey="key"
                      pagination={false}
                      loading={false}
                      page={0}
                      total={fields.length}
                      pageSize={fields.length}
                      scroll={{ x: 300 }}
                      columns={[
                        {
                          key: 'from',
                          title: messages['common.from'],
                          dataIndex: 'from',
                          render: (_: any, field: any) => (
                            <Form.Item
                              name={[field.name, 'from']}
                              rules={[
                                {
                                  required: true,
                                  message: messages['form.pleaseEnter'],
                                },
                                {
                                  validator: async (_: any, value: any) => {
                                    const list =
                                      form.getFieldValue('tieredFees') || [];
                                    const index = field.name as number;
                                    if (value === undefined || value === '')
                                      return Promise.resolve();
                                    const fromNum = toNumber(value);
                                    const toVal = list?.[index]?.to;
                                    if (toVal !== undefined && toVal !== '') {
                                      const toNum = toNumber(toVal);
                                      if (
                                        !Number.isNaN(fromNum) &&
                                        !Number.isNaN(toNum) &&
                                        fromNum > toNum
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            'From cannot be greater than To',
                                          ),
                                        );
                                      }
                                    }
                                    if (index > 0) {
                                      const prevToVal = list?.[index - 1]?.to;
                                      if (
                                        prevToVal !== undefined &&
                                        prevToVal !== ''
                                      ) {
                                        const prevTo = toNumber(prevToVal);
                                        if (
                                          !Number.isNaN(prevTo) &&
                                          !Number.isNaN(fromNum) &&
                                          fromNum <= prevTo
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'From must be greater than previous To',
                                            ),
                                          );
                                        }
                                      }
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              className="mb-0"
                            >
                              <AppRateInput
                                placeholder={messages['common.enter']}
                              />
                            </Form.Item>
                          ),
                        },
                        {
                          key: 'to',
                          title: messages['common.to'],
                          dataIndex: 'to',
                          render: (_: any, field: any) => (
                            <Form.Item
                              name={[field.name, 'to']}
                              rules={[
                                {
                                  required: true,
                                  message: messages['form.pleaseEnter'],
                                },
                                {
                                  validator: async (_: any, value: any) => {
                                    const list =
                                      form.getFieldValue('tieredFees') || [];
                                    const index = field.name as number;
                                    if (value === undefined || value === '')
                                      return Promise.resolve();
                                    const toNum = toNumber(value);
                                    const fromVal = list?.[index]?.from;
                                    if (
                                      fromVal !== undefined &&
                                      fromVal !== ''
                                    ) {
                                      const fromNum = toNumber(fromVal);
                                      if (
                                        !Number.isNaN(fromNum) &&
                                        !Number.isNaN(toNum) &&
                                        toNum < fromNum
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            'To cannot be less than From',
                                          ),
                                        );
                                      }
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              className="mb-0"
                            >
                              <AppRateInput
                                placeholder={messages['common.enter']}
                              />
                            </Form.Item>
                          ),
                        },
                        {
                          key: 'fee',
                          title: messages['common.fee'],
                          dataIndex: 'fee',
                          render: (_: any, field: any) => (
                            <Form.Item
                              name={[field.name, 'fee']}
                              rules={[
                                {
                                  required: true,
                                  message: messages['form.pleaseEnter'],
                                },
                              ]}
                              className="mb-0"
                            >
                              <AppCurrencyInput
                                placeholder={messages['common.enter']}
                              />
                            </Form.Item>
                          ),
                        },
                        {
                          key: 'actions',
                          title: ' ',
                          dataIndex: 'actions',
                          width: 65,
                          fixed: 'right',
                          align: 'left',
                          render: (_: any, field: any) => (
                            <div
                              className="flex items-center"
                              onClick={() => {
                                remove(field.name);
                              }}
                            >
                              <Icons.IconDelete />
                            </div>
                          ),
                        },
                      ]}
                      titleEmpty={messages['common.tieredFee.empty']}
                    />
                  </div>
                );
              }}
            </Form.List>
          </AppCard>
        </AppFormContainer>
      )}
    </AppModalContainer>
  );
};

export default TieredFeeTableModal;
